import { FormModal } from '@/components/form-modal.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { Button } from '@/components/ui/button.js';
import { DialogDescription } from '@/components/ui/dialog.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { deactivateSchool, getSchool, invalidateSchoolQueries } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

const formSchema = z.object({
  confirmed: z.boolean().default(false),
});

export function DeactivateSchool({ schoolId, schoolName }: { schoolId: string; schoolName: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: deactivateSchool,
  });

  const onSubmit = async ({
    formData,
    closeModal,
  }: {
    formData: z.infer<typeof formSchema>;
    closeModal: () => void;
  }) => {
    if (!formData.confirmed) {
      toast({
        title: 'Please confirm that you want to deactivate this school before continuing.',
      });
      return;
    }

    try {
      await mutateAsync(schoolId);
      closeModal();
      toast({ title: `${schoolName} is now inactive` });
      queryClient.invalidateQueries(getSchool(schoolId));
      invalidateSchoolQueries(queryClient);
    } catch (e) {
      const error = airServerErrorFrom(e);
      toast({
        variant: 'destructive',
        title: 'Failed to deactivate school',
        description: error?.userFacingMessage,
      });
    }
  };

  return (
    <FormModal
      modalTitle="Deactivate school"
      dataTestId="school-modal-deactivate"
      trigger={
        <Button type="button" variant="outline" data-testid="school-button-deactivate">
          Deactivate
        </Button>
      }
      onSubmit={onSubmit}
      dialogContentProps={{ className: 'max-w-md' }}
      submitButtonProps={{
        children: 'Deactivate',
        loading: isPending,
      }}
      formSchema={formSchema}
      confirmBtnLabel="Deactivate"
      defaultValues={{ confirmed: false }}
    >
      {({ form }) => (
        <div className="my-6 space-y-4">
          <DialogDescription>
            {schoolName} will stay in historical records with status inactive. It will no longer appear on the default
            school list.
          </DialogDescription>
          <FormCheckbox
            name="confirmed"
            label="I understand this school will be marked inactive"
            form={form}
            horizontal
          />
        </div>
      )}
    </FormModal>
  );
}
